import { Button, Dialog, Paragraph, XStack, YStack } from 'tamagui';
import type { ServiceLogEntry } from '../lib/api';

// Minimum tap-target size (px) required on mobile for the dialog controls.
const MIN_TAP_TARGET_PX = 44;

/**
 * Confirmation dialog for permanently deleting a single service-log entry.
 * Guards the per-row Delete on VehicleDetail so an accidental tap cannot destroy
 * maintenance history. Mirrors the DeleteVehicleDialog pattern (Tamagui Dialog +
 * title + Cancel + danger confirm), but with copy scoped to one log — NOT the
 * vehicle-cascade wording. Open when `log` is non-null.
 */
export function DeleteServiceLogDialog({
  log,
  onCancel,
  onConfirm,
  isPending,
}: {
  log: Pick<ServiceLogEntry, 'serviceType' | 'date' | 'mileage'> | null;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog modal open={log !== null} onOpenChange={(o) => (o ? undefined : onCancel())}>
      {/*
        aria-label goes on Dialog.Portal, NOT Dialog.Content: Tamagui renders the
        Portal frame as a native <dialog> element (styled YStack, tag "dialog"),
        which is the node carrying role=dialog. Its built-in aria-labelledby sits
        on the inner content div, so the dialog element itself (named only by its
        own aria-label/labelledby/title) is otherwise unnamed. Labelling the
        Portal names the actual role=dialog node.
      */}
      <Dialog.Portal aria-label="Delete this service log?">
        <Dialog.Overlay key="overlay" opacity={0.5} />
        <Dialog.Content key="content" elevate padding="$4" width={440} maxWidth="90vw" gap="$3">
          <Dialog.Title>Delete this service log?</Dialog.Title>
          {log ? (
            <YStack gap="$2">
              <Paragraph>
                <strong>{log.serviceType}</strong> · {new Date(log.date).toLocaleDateString()} ·{' '}
                {log.mileage.toLocaleString()}
              </Paragraph>
              <Paragraph color="$danger">
                This permanently deletes this service-log entry. This cannot be undone.
              </Paragraph>
            </YStack>
          ) : null}
          <XStack gap="$2" justifyContent="flex-end">
            <Button
              className="dialog-action"
              minHeight={MIN_TAP_TARGET_PX}
              minWidth={MIN_TAP_TARGET_PX}
              focusStyle={{ outlineColor: '$accent', outlineStyle: 'solid', outlineWidth: 2 }}
              onPress={onCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              className="dialog-action"
              theme="red"
              minHeight={MIN_TAP_TARGET_PX}
              minWidth={MIN_TAP_TARGET_PX}
              focusStyle={{ outlineColor: '$accent', outlineStyle: 'solid', outlineWidth: 2 }}
              onPress={onConfirm}
              disabled={isPending}
            >
              {isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
